import { useIntl } from "react-intl";

import { BackButton } from "@/components/BackButton/BackButton";
import { IconButton } from "@/components/IconButton/IconButton";
import {
  CaretLeftIcon,
  CaretRightIcon,
} from "@/components/Icons/rtlReadyIcons";
import { LazyImage } from "@/components/Image/LazyImage";
import { useScrollCarousel } from "@/hooks/useScrollCarousel";

import { HotelGalleryThumbnails } from "./HotelGalleryThumbnails";

type HotelGalleryProps = {
  initialIndex: number;
  onBackClick: () => void;
  images: { url: string; name: string }[];
};

export function HotelGallery({
  images,
  initialIndex,
  onBackClick,
}: HotelGalleryProps) {
  const intl = useIntl();
  const { ref, index, handleScroll, changeIndex } =
    useScrollCarousel(initialIndex);

  return (
    // z-index is above the WhatsApp button, see MUI z-index scale
    <div className="bg-opacity-80 fixed start-0 top-0 z-1280 flex h-full w-full flex-col bg-black">
      <div className="flex items-center justify-between gap-2 p-4">
        <BackButton iconClassName="fill-white" onClick={onBackClick} />
        <div className="text-sm text-white">
          {index + 1}/{images.length}
        </div>
        <div className="size-10" />
      </div>

      <div className="relative flex min-h-0 w-full shrink grow">
        <ul
          onScroll={handleScroll}
          ref={ref}
          className="scrollbar-none flex h-full w-full snap-x snap-mandatory flex-nowrap overflow-x-auto"
        >
          {images.map(({ url, name }, idx) => (
            <li
              key={url}
              className="relative flex h-full w-full shrink-0 grow-0 snap-center items-center justify-center p-4"
            >
              <LazyImage
                src={url}
                alt={intl.formatMessage(
                  {
                    defaultMessage: "{name} - Image {number}",
                    description: "Alt text for image in gallery",
                  },
                  { name, number: idx + 1 },
                )}
                rootMargin="0px 100px 0px 100px"
                className="max-h-full max-w-full rounded-lg object-contain object-center"
              />
            </li>
          ))}
        </ul>

        {images.length > 1 && (
          <>
            {index > 0 && (
              <div className="absolute start-5 top-1/2 z-50 -translate-y-1/2 rounded-full bg-white/70">
                <IconButton
                  onClick={() => changeIndex(index - 1)}
                  aria-label={intl.formatMessage({
                    defaultMessage: "Previous image",
                    description:
                      "ARIA label for button to navigate to previous image in gallery",
                  })}
                >
                  <CaretLeftIcon size={28} />
                </IconButton>
              </div>
            )}
            {index < images.length - 1 && (
              <div className="absolute end-5 top-1/2 z-50 -translate-y-1/2 rounded-full bg-white/70">
                <IconButton
                  onClick={() => changeIndex(index + 1)}
                  aria-label={intl.formatMessage({
                    defaultMessage: "Next image",
                    description:
                      "ARIA label for button to navigate to next image in gallery",
                  })}
                >
                  <CaretRightIcon size={28} />
                </IconButton>
              </div>
            )}
          </>
        )}
      </div>

      <HotelGalleryThumbnails
        images={images}
        activeIndex={index}
        onSelect={changeIndex}
      />
    </div>
  );
}
